/**
 * Renderer, scene, lights and fog.
 *
 * The look is a shaft of weather that changes as you climb. Each of the six
 * zones owns a background colour, a fog colour and density, and a hemisphere
 * pair (see {@link ZONE_SKY}); {@link updateSky} crossfades between them across
 * the zone boundaries, so leaving Sunken Ruins for Crystal Hollow is something
 * you see rather than something a toast tells you. Background and fog always
 * share a colour, which is what makes the abyss under the course fade to nothing
 * and read as *tall* — the density is the dial for how far you can see down it.
 *
 * A hemisphere light does the ambient work; one warm directional light casts the
 * shadows that make a 0.4 m ledge legible from above. Shadows follow the player
 * rather than covering the course: a 60 m orthographic box snapped to 2 m
 * increments (see {@link updateShadowTarget}). Snapping is not an optimisation —
 * an unsnapped shadow camera makes every shadow edge crawl as you move.
 */

import {
  Color,
  DirectionalLight,
  FogExp2,
  HemisphereLight,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';

import { ZONE_BOUNDARY_YS } from '@shared/level';

/** One zone's weather: background, fog colour, and the hemisphere light's pair. */
interface SkyStop {
  sky: number;
  fog: number;
  hemiSky: number;
  hemiGround: number;
}

/**
 * The six zones' skies, bottom to top. Fog density is the reach dial: the Meadow
 * Foothills open wide so the tutorial can see where it is going, the Crystal
 * Hollow closes in, and the Obsidian Crown opens out again so the summit can see
 * how far it has come.
 */
export const ZONE_SKY: readonly SkyStop[] = [
  { sky: 0x2a3a2a, fog: 0.0042, hemiSky: 0xb8cc96, hemiGround: 0x3a4a2c }, // Meadow — bright open morning
  { sky: 0x1c2a24, fog: 0.0048, hemiSky: 0x93b08a, hemiGround: 0x2c3624 }, // Verdant — green-grey dusk
  { sky: 0x2a2419, fog: 0.0044, hemiSky: 0xc4ae83, hemiGround: 0x3a2e1e }, // Ruins — warm dust
  { sky: 0x1e1832, fog: 0.0052, hemiSky: 0x9a8ad0, hemiGround: 0x2a2140 }, // Crystal — violet cave, densest fog
  { sky: 0x27394a, fog: 0.0036, hemiSky: 0xbcd6e8, hemiGround: 0x36495c }, // Frozen — thin pale high air
  { sky: 0x0b0a11, fog: 0.0024, hemiSky: 0x8a90a8, hemiGround: 0x191620 }, // Obsidian — near-black crown
];
/**
 * Half-width of the crossfade around each boundary, in metres. Wide enough that
 * a bounce back and forth across the line does not strobe the sky, narrow enough
 * that the lobby floor and its new colour arrive together.
 */
const SKY_BLEND_M = 12;
/** Move less than this since the last blend and the sky cannot have changed visibly. */
const SKY_STEP_M = 0.25;
/** Half-width of the shadow camera's orthographic box, in metres. */
const SHADOW_EXTENT = 30;
/** Distance the sun sits from its target. */
const SUN_DISTANCE = 90;
/** Shadow-camera snap grid. Anything finer shimmers. */
const SHADOW_SNAP = 2;

/** Direction from the shadow target to the sun (normalised at module load). */
const SUN_DIR = new Vector3(0.45, 0.85, 0.3).normalize();

export interface SceneBundle {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  /** The shadow-casting sun. Pass it to {@link updateShadowTarget} each frame. */
  sun: DirectionalLight;
  /** Sky/ground fill. Exposed so a settings screen can dim it. */
  hemi: HemisphereLight;
  /** Call on `window.resize`. */
  onResize(): void;
}

/**
 * Create the renderer and scene and attach the canvas to `#app`.
 *
 * Throws when `#app` is missing — that is a broken `index.html`, not a runtime
 * condition worth degrading gracefully for.
 */
export function createScene(): SceneBundle {
  const app = document.getElementById('app');
  if (app === null) throw new Error('#app is missing from index.html');

  const renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  app.appendChild(renderer.domElement);

  const base = ZONE_SKY[0] as SkyStop;
  const scene = new Scene();
  scene.background = new Color(base.sky);
  scene.fog = new FogExp2(base.sky, base.fog);

  const camera = new PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 600);
  camera.position.set(0, 6, 24);
  camera.lookAt(0, 3, 0);

  const hemi = new HemisphereLight(base.hemiSky, base.hemiGround, 0.7);
  scene.add(hemi);

  const sun = new DirectionalLight(0xffe6bc, 1.1);
  sun.position.copy(SUN_DIR).multiplyScalar(SUN_DISTANCE);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -SHADOW_EXTENT;
  sun.shadow.camera.right = SHADOW_EXTENT;
  sun.shadow.camera.top = SHADOW_EXTENT;
  sun.shadow.camera.bottom = -SHADOW_EXTENT;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = SUN_DISTANCE + SHADOW_EXTENT * 3;
  sun.shadow.bias = -0.0008;
  sun.shadow.normalBias = 0.02;
  sun.shadow.camera.updateProjectionMatrix();
  scene.add(sun);
  scene.add(sun.target);

  function onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
  }

  return { renderer, scene, camera, sun, hemi, onResize };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Hermite ease between two edges — the classic `smoothstep`. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Last height the sky was blended for. `Infinity` forces the first call through. */
let _lastSkyY = Infinity;
/** Scratch for the far end of every colour lerp — `updateSky` allocates nothing. */
const _skyMix = new Color();

/**
 * Crossfade the background, fog and ambient light to the zone at height `y`.
 *
 * The zone index is computed as a *fraction* rather than looked up: summing a
 * `smoothstep` over every boundary gives a continuous 0…4 that eases through
 * each crossing on its own, with no branching and nothing to get stuck on when
 * the player bounces back and forth across a line.
 *
 * Only uniforms are touched. Swapping `scene.fog` for a new object — or for a
 * different fog *type* — invalidates every material and recompiles the shaders,
 * which is a visible hitch in the middle of a climb.
 */
export function updateSky(scene: Scene, hemi: HemisphereLight, y: number): void {
  if (Math.abs(y - _lastSkyY) < SKY_STEP_M) return;
  _lastSkyY = y;

  let t = 0;
  for (const boundary of ZONE_BOUNDARY_YS) {
    t += smoothstep(boundary - SKY_BLEND_M, boundary + SKY_BLEND_M, y);
  }
  const i = Math.min(ZONE_SKY.length - 2, Math.floor(t));
  const f = t - i;
  const lo = ZONE_SKY[i] as SkyStop;
  const hi = ZONE_SKY[i + 1] as SkyStop;

  const background = scene.background;
  if (background instanceof Color) background.setHex(lo.sky).lerp(_skyMix.setHex(hi.sky), f);
  const fog = scene.fog;
  if (fog instanceof FogExp2) {
    fog.color.setHex(lo.sky).lerp(_skyMix.setHex(hi.sky), f);
    fog.density = lo.fog + (hi.fog - lo.fog) * f;
  }
  hemi.color.setHex(lo.hemiSky).lerp(_skyMix.setHex(hi.hemiSky), f);
  hemi.groundColor.setHex(lo.hemiGround).lerp(_skyMix.setHex(hi.hemiGround), f);
}

const _snapped = new Vector3();

/**
 * Point the sun's shadow box at `target` (normally the player).
 *
 * Snapped to {@link SHADOW_SNAP} metre increments so the shadow map's texel grid
 * stays put between frames; without this every edge in the scene crawls while
 * you move. Cheap to call every frame — it early-outs when nothing moved.
 */
export function updateShadowTarget(light: DirectionalLight, target: Vector3): void {
  _snapped.set(
    Math.round(target.x / SHADOW_SNAP) * SHADOW_SNAP,
    Math.round(target.y / SHADOW_SNAP) * SHADOW_SNAP,
    Math.round(target.z / SHADOW_SNAP) * SHADOW_SNAP,
  );
  if (light.target.position.equals(_snapped)) return;
  light.target.position.copy(_snapped);
  light.target.updateMatrixWorld();
  light.position.copy(_snapped).addScaledVector(SUN_DIR, SUN_DISTANCE);
  light.updateMatrixWorld();
}
